import { arrayToTree, useTranslation } from "@opendash/core";
import { Select, TreeSelect } from "antd";
import { DefaultOptionType } from "antd/es/select";
import Parse from "parse";
import React, { useEffect, useMemo, useState } from "react";

type Props<T extends Parse.Object> = PropsSingle<T> | PropsMultiple<T>;

type DefaultProps<T extends Parse.Object> =
  | {
      mode?: "single" | "multiple";
      reload?: any;
      translate?: boolean;
      type: new () => T;
      getLabel?: (value: T) => string;
      getValue?: (value: T) => string;
      onQuery?: (query: Parse.Query<T>) => Promise<void> | void;
      style?: React.CSSProperties;
    }
  | {
      translate?: boolean;
      type: string;
      getLabel?: (value: Parse.Object) => string;
      getValue?: (value: Parse.Object) => string;
      onQuery?: (query: Parse.Query<Parse.Object>) => Promise<void> | void;
      style?: React.CSSProperties;
    };

type PropsSingle<T extends Parse.Object> = DefaultProps<T> &
  ConditionalPropsTree<T> & {
    mode?: "single";
    value?: T;
    onChange?: (value: T) => void;
    reload?: any;
  };

type ConditionalPropsTree<T extends Parse.Object> =
  | {
      tree?: true;
      getParentId: (value: T) => any;
      getId: (value: T) => any;
    }
  | {
      tree?: false;
      getParentId?: never;
      getId?: never;
    };

type PropsMultiple<T extends Parse.Object> = DefaultProps<T> &
  ConditionalPropsTree<T> & {
    mode?: "multiple";
    value?: T[];
    onChange?: (value: T[]) => void;
    reload?: any;
  };

/**
 * Fetches all objects of a given type and displays them in a select dropdown.
 * Query value and label can be customized.
 * @returns
 */
export const ParseObjectSelect = <T extends Parse.Object>(props: Props<T>) => {
  const t = useTranslation();
  const [internalValue, setInternalValue] = useState<any>(undefined);
  // Determine the effective value based on props.value or internalValue
  const effectiveValue = props.value ?? internalValue;
  const effectiveOnChange = props.onChange ?? setInternalValue;
  const getValue = props.getValue ?? ((value: T) => value?.id);
  const getLabel =
    props.getLabel ??
    ((value: T) =>
      props.translate
        ? t(`parse:${value.className}.label`, { ...value.attributes })
        : value?.id);

  if (props.mode === "multiple") {
    return (
      <ParseObjectSelectMultiple
        {...props}
        value={effectiveValue}
        onChange={effectiveOnChange}
        getLabel={getLabel}
        getValue={getValue}
      />
    );
  }
  return (
    <ParseObjectSelectSingle
      {...props}
      value={effectiveValue}
      getLabel={getLabel}
      getValue={getValue}
      onChange={effectiveOnChange}
    />
  );
};

const ParseObjectSelectSingle = <T extends Parse.Object>(
  props: PropsSingle<T>
) => {
  const [objects, setObjects] = useState<T[]>([]);

  useEffect(() => {
    async function fetchObjects() {
      const query = new Parse.Query(props.type);
      props.onQuery && (await props.onQuery(query));
      query.find().then((results) => {
        setObjects(results);
      });
    }

    fetchObjects();
  }, [props.reload]);

  const options: DefaultOptionType[] = useMemo(() => {
    if (props.tree && props.getParentId) {
      const tree = arrayToTree(objects, props.getParentId, props.getId);
      return tree.map((value) =>
        treeMap(value, props.getLabel, props.getValue)
      );
    }
    return objects.map((object) => ({
      label: props.getLabel(object),
      value: props.getValue(object),
    }));
  }, [objects, props.tree, props.getParentId, props.getId]);

  return (
    <>
      <Select
        showSearch
        options={options}
        value={props.getValue(props.value)}
        onChange={(value) =>
          props.onChange &&
          props.onChange(objects.find((obj) => props.getValue(obj) === value))
        }
        style={props.style}
      />
      {props.tree && (
        <TreeSelect
          showSearch
          allowClear
          treeData={options}
          value={props.getValue(props.value)}
          onChange={(value) =>
            props.onChange &&
            props.onChange(objects.find((obj) => props.getValue(obj) === value))
          }
          style={props.style}
        />
      )}
    </>
  );
};

const ParseObjectSelectMultiple = <T extends Parse.Object>(
  props: PropsMultiple<T>
) => {
  const [objects, setObjects] = useState<T[]>([]);

  useEffect(() => {
    const query = new Parse.Query(props.type);
    query.find().then((results) => {
      setObjects(results);
    });
  }, [props.reload]);

  const options: DefaultOptionType[] = useMemo(() => {
    if (props.tree && props.getParentId) {
      const tree = arrayToTree(objects, props.getParentId, props.getId);
      return tree.map((value) =>
        treeMap(value, props.getLabel, props.getValue)
      );
    }
    return objects.map((object) => ({
      label: props.getLabel(object),
      value: props.getValue(object),
    }));
  }, [objects]);

  return (
    <>
      {!props.tree && (
        <Select
          showSearch
          mode="multiple"
          options={options}
          value={props.value?.map((value) => props.getValue(value))}
          onChange={(value) =>
            props.onChange &&
            props.onChange(
              objects.filter((obj) => value.includes(props.getValue(obj)))
            )
          }
          style={props.style}
        />
      )}
      {props.tree && (
        <TreeSelect
          showSearch
          allowClear
          multiple
          treeData={options}
          value={props.value?.map((value) => props.getValue(value))}
          onChange={(value) =>
            props.onChange &&
            props.onChange(
              objects.filter((obj) => value.includes(props.getValue(obj)))
            )
          }
          style={props.style}
        />
      )}
    </>
  );
};

// Helper functions
/**
 * Mapping an object and its children to DefaultOptionType
 */
function treeMap<T extends object & { children: T[] }>(
  value: T,
  getLabel: any,
  getValue: any
): DefaultOptionType {
  return {
    label: getLabel(value),
    value: getValue(value),
    children: value.children.map((value) => treeMap(value, getLabel, getValue)),
  };
}
